// Global event ranking — the same order for everyone looking at a region.
// See plans/event-ranking.md.
//
// curatorScore (Claude's 0–100 draw estimate, one absolute scale) is the prior.
// Real interest takes over as it accumulates: it's ranked as a percentile within
// the pool, because user counts differ wildly between metros while the curator
// score doesn't need normalizing. beaconFit keeps ranked picks on-profile:
// 'off' events are never promoted, 'adjacent' ones are discounted.

export interface RankableEvent {
  id: string
  startDate: string
  curatorScore?: number
  goingCount?: number
  interestedCount?: number
  beaconFit?: 'core' | 'adjacent' | 'off'
}

/** Applied to the global score by fit. Missing fit counts as core, so unclassified events are never hidden. */
export const FIT_MULTIPLIER = { core: 1, adjacent: 0.8, off: 0 } as const

const fitOf = (e: RankableEvent) => FIT_MULTIPLIER[e.beaconFit ?? 'core']
const isOffProfile = (e: RankableEvent) => e.beaconFit === 'off'

/** Interactions at which real interest fully replaces the curator prior. */
export const INTEREST_SATURATION = 15

/** Prior for a pool with no curator scores at all. */
const DEFAULT_PRIOR = 40

const demand = (e: RankableEvent) => 2 * (e.goingCount ?? 0) + (e.interestedCount ?? 0)
const interactions = (e: RankableEvent) => (e.goingCount ?? 0) + (e.interestedCount ?? 0)

/** How far real interest has replaced the curator prior, 0–1. */
export const interestWeight = (e: RankableEvent) => Math.min(1, interactions(e) / INTEREST_SATURATION)

function median(values: number[]): number | undefined {
  if (values.length === 0) return undefined
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
}

/** Percentile (0–100) of each event's demand within the pool; ties share their average rank. */
function demandPercentiles<T extends RankableEvent>(events: T[]): Map<string, number> {
  const out = new Map<string, number>()
  if (events.length === 1) {
    out.set(events[0].id, 100)
    return out
  }
  const sorted = [...events].sort((a, b) => demand(a) - demand(b))
  let i = 0
  while (i < sorted.length) {
    let j = i
    while (j + 1 < sorted.length && demand(sorted[j + 1]) === demand(sorted[i])) j++
    const pct = (((i + j) / 2) / (sorted.length - 1)) * 100
    for (let k = i; k <= j; k++) out.set(sorted[k].id, pct)
    i = j + 1
  }
  return out
}

/**
 * Global score (0–100) per event, relative to the pool it's ranked in. An event
 * with no curatorScore yet takes the pool's median, so it's neither buried nor
 * promoted until it's scored. Discounted by beaconFit.
 */
export function globalScores<T extends RankableEvent>(events: T[]): Map<string, number> {
  const prior = median(events.flatMap(e => (typeof e.curatorScore === 'number' ? [e.curatorScore] : []))) ?? DEFAULT_PRIOR
  const pct = demandPercentiles(events)
  const out = new Map<string, number>()
  for (const e of events) {
    const weight = interestWeight(e)
    const base = typeof e.curatorScore === 'number' ? e.curatorScore : prior
    out.set(e.id, ((1 - weight) * base + weight * (pct.get(e.id) ?? 0)) * fitOf(e))
  }
  return out
}

/** On-profile events ordered best-first by global score; ties go to the earlier event. 'off' events are left out. */
export function rankEvents<T extends RankableEvent>(events: T[]): T[] {
  const scores = globalScores(events)
  return events.filter(e => !isOffProfile(e)).sort(
    (a, b) => (scores.get(b.id) ?? 0) - (scores.get(a.id) ?? 0) || a.startDate.localeCompare(b.startDate),
  )
}

/** Share of a region's ranked events that earn the Popular badge. */
export const POPULAR_SHARE = 0.1
/** Real interest a Popular event needs (8 interactions) — curatorScore alone never earns it. */
export const POPULAR_MIN_WEIGHT = 0.5

/** The pool's top ~10% by global score, keeping only those with real interest behind them. */
export function popularEventIds<T extends RankableEvent>(pool: T[]): Set<string> {
  if (pool.length === 0) return new Set()
  const top = rankEvents(pool).slice(0, Math.max(1, Math.round(pool.length * POPULAR_SHARE)))
  return new Set(top.filter(e => interestWeight(e) >= POPULAR_MIN_WEIGHT).map(e => e.id))
}

/**
 * The Fri–Sun weekend Destacados features, as YYYY-MM-DD bounds. Mon–Thu point
 * at the coming weekend; Fri–Sun at the one in progress, from today on.
 */
export function featuredWeekend(today: string): { start: string; end: string } {
  const d = new Date(`${today}T12:00:00Z`)
  const dow = d.getUTCDay() // 0 = Sun … 6 = Sat
  const daysToSunday = (7 - dow) % 7
  const sunday = new Date(d)
  sunday.setUTCDate(d.getUTCDate() + daysToSunday)
  const friday = new Date(sunday)
  friday.setUTCDate(sunday.getUTCDate() - 2)
  const iso = (x: Date) => x.toISOString().slice(0, 10)
  return { start: dow === 0 || dow >= 5 ? today : iso(friday), end: iso(sunday) }
}

/** Score added per lineup artist the viewer follows. */
export const ARTIST_MATCH_BOOST = 15
/** Matches past this many add nothing — three of your artists is already a strong signal. */
export const ARTIST_MATCH_CAP = 3

/**
 * Events ordered best-first for one viewer: the global score plus a boost per
 * followed artist on the lineup. With no matches this is exactly rankEvents.
 * An 'off' event comes back only when the viewer follows someone on it — then
 * it's theirs, whatever the genre.
 */
export function rankForViewer<T extends RankableEvent>(events: T[], artistMatches: Map<string, number>): T[] {
  const scores = globalScores(events)
  const matched = (e: T) => Math.min(ARTIST_MATCH_CAP, artistMatches.get(e.id) ?? 0)
  const score = (e: T) =>
    isOffProfile(e) ? ARTIST_MATCH_BOOST * matched(e) : (scores.get(e.id) ?? 0) + ARTIST_MATCH_BOOST * matched(e)
  return events.filter(e => !isOffProfile(e) || matched(e) > 0).sort((a, b) => score(b) - score(a) || a.startDate.localeCompare(b.startDate))
}

/** Applied to events from a neighbouring region in a region's list — they still show, below comparable local ones. */
export const NEARBY_REGION_FACTOR = 0.85

/** Score added per friend going to or interested in an event — a nudge, not a takeover. */
export const CREW_BOOST = 5
/** Friends past this many add nothing; the crew boost tops out at one followed artist's worth. */
export const CREW_CAP = 3

/**
 * The whole list in relevance order — unlike rankEvents nothing is dropped:
 * 'off' events sink to the bottom (their fit zeroes the global score), and
 * events outside `isLocal` are discounted. Followed artists boost as in
 * rankForViewer, and friends going/interested (`crewCounts`) add a smaller
 * boost; pass empty maps for the plain global order.
 */
export function sortByRelevance<T extends RankableEvent>(
  events: T[],
  artistMatches: Map<string, number>,
  isLocal: (event: T) => boolean = () => true,
  crewCounts: Map<string, number> = new Map(),
): T[] {
  const scores = globalScores(events)
  const score = (e: T) =>
    ((scores.get(e.id) ?? 0) +
      ARTIST_MATCH_BOOST * Math.min(ARTIST_MATCH_CAP, artistMatches.get(e.id) ?? 0) +
      CREW_BOOST * Math.min(CREW_CAP, crewCounts.get(e.id) ?? 0)) *
    (isLocal(e) ? 1 : NEARBY_REGION_FACTOR)
  return [...events].sort((a, b) => score(b) - score(a) || a.startDate.localeCompare(b.startDate))
}

export type DateRange = 'today' | 'weekend' | 'week' | 'month' | 'all'
export const DATE_RANGES: DateRange[] = ['today', 'weekend', 'week', 'month', 'all']

const addDays = (iso: string, n: number) => {
  const d = new Date(`${iso}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

/** YYYY-MM-DD bounds for a range, inclusive; `end` null = open-ended. Week and month are rolling windows from today. */
export function dateRangeBounds(range: DateRange, today: string): { start: string; end: string | null } {
  switch (range) {
    case 'today': return { start: today, end: today }
    case 'weekend': return featuredWeekend(today)
    case 'week': return { start: today, end: addDays(today, 6) }
    case 'month': return { start: today, end: addDays(today, 29) }
    case 'all': return { start: today, end: null }
  }
}

/** Whether an event runs on any day of the range (a multi-day festival overlapping it counts). */
export function inDateRange(event: { startDate: string; endDate?: string }, bounds: { start: string; end: string | null }): boolean {
  const first = event.startDate.slice(0, 10)
  const last = (event.endDate ?? event.startDate).slice(0, 10)
  return last >= bounds.start && (bounds.end === null || first <= bounds.end)
}
